<template>
  <div :class="{ 'daily-work--modal-open': showRemarkModal }">
  <component :is="rootComponent" v-bind="rootProps">
    <template v-if="!embedded" #header-control>
      <woot-button
        v-tooltip.top="'Reload'"
        icon="arrow-clockwise"
        size="small"
        variant="smooth"
        color-scheme="secondary"
        class="daily-work-reload-btn"
        :is-loading="isLoading"
        aria-label="Reload"
        @click="reloadTasks"
      />
    </template>
    <template v-if="!embedded" #control>
      <div class="daily-work-toolbar relative">
        <div
          v-if="isAdmin"
          class="multiselect-wrap--small daily-work-agent-filter z-[3]"
        >
          <multiselect
            v-model="selectedUser"
            :options="userFilterOptions"
            track-by="id"
            label="name"
            placeholder="Select a user"
            :allow-empty="false"
            :show-labels="false"
            class="no-margin"
            @input="onUserFilterChange"
          />
        </div>
        <woot-button
          v-tooltip.top="'Open full view'"
          icon="fullscreen"
          variant="smooth"
          size="small"
          color-scheme="secondary"
          class-names="daily-work-full-view-btn"
          view-box="-4 -4 24 24"
          aria-label="Open full view"
          @click="openFullView"
        />
      </div>
    </template>

    <div
      class="daily-work-container relative w-full"
      :class="{ 'daily-work-container--embedded': embedded }"
    >
      <div class="daily-work-table-wrap relative">
        <ve-table
          v-if="displayTableData.length > 0"
          style="height: 100%;"
          :fixed-header="true"
          :columns="columns"
          :table-data="displayTableData"
          :row-style-option="rowStyleOption"
          :cell-span-option="cellSpanOption"
          :event-custom-option="eventCustomOption"
        />
        <p
          v-else-if="!isLoading"
          class="daily-work-empty-overlay"
        >
          No tasks.
        </p>
      </div>
      <div class="daily-work-footer">
        <div class="daily-work-next-task">
          <span class="daily-work-next-task__label">Next Task:</span>
          <span
            class="daily-work-next-task__timer"
            v-tooltip.top="embedded ? nextFillTimeFull : null"
          >{{ nextFillTime }}</span>
        </div>
        <div v-if="meta.total_count > 0" class="table-pagination">
          <ve-pagination
            :total="meta.total_count"
            :page-index="pageIndex"
            :page-size="pageSize"
            :page-size-option="[pageSize]"
            @on-page-number-change="onPageChange"
          />
        </div>
      </div>

      <div v-if="isLoading" class="daily-work-loader-overlay">
        <div class="daily-work-loader-content">
          <spinner size="48" />
          <span class="ml-4 font-bold text-base">Loading daily tasks</span>
        </div>
      </div>
    </div>
  </component>

    <woot-modal
      :show.sync="showRemarkModal"
      :on-close="closeRemarkModal"
      portal-to-body
      content-class="daily-work-remark-modal"
    >
      <div class="h-auto overflow-auto flex flex-col">
        <woot-modal-header
          :header-title="isViewingRemark ? 'Task Remark' : 'Complete Task'"
          :header-content="
            isViewingRemark
              ? 'Remark added when this task was completed.'
              : 'Add a remark before marking this task complete.'
          "
        />
        <form class="w-full modal-content" @submit.prevent="submitRemark">
          <div v-if="!isViewingRemark && completionTypeOptions.length > 1" class="daily-work-complete-types">
            <div class="daily-work-complete-types__header">
              <span>Complete</span>
              <button
                type="button"
                class="daily-work-complete-types__toggle"
                @click.prevent="toggleAllCompletionTypes"
              >
                {{ allCompletionTypesSelected ? 'Clear all' : 'Select all' }}
              </button>
            </div>
            <label
              v-for="option in completionTypeOptions"
              :key="option.value"
              class="daily-work-complete-type-option flex items-center gap-2"
            >
              <input
                v-model="selectedCompletionTypes"
                type="checkbox"
                class="daily-work-complete-type-option__checkbox"
                :value="option.value"
              />
              <span class="daily-work-complete-type-option__label">{{
                option.label
              }}</span>
            </label>
          </div>
          <label class="daily-work-remark-field">
            Remark
            <textarea
              v-model.trim="completionRemark"
              class="daily-work-remark-input"
              rows="4"
              maxlength="1000"
              :readonly="isViewingRemark"
              :placeholder="isViewingRemark ? 'No remark' : 'Enter remark'"
            />
          </label>
          <div class="flex flex-row justify-end gap-2 py-2">
            <woot-button
              variant="clear"
              :disabled="!!completingId"
              @click.prevent="closeRemarkModal"
            >
              {{ isViewingRemark ? 'Close' : 'Cancel' }}
            </woot-button>
            <woot-button
              v-if="!isViewingRemark"
              :is-loading="!!completingId"
              :disabled="!canSubmitCompletion || !!completingId"
              @click.prevent="submitRemark"
            >
              Complete
            </woot-button>
          </div>
        </form>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import { h } from 'vue';
import { mapGetters } from 'vuex';
import { VeTable, VePagination } from 'vue-easytable';
import Multiselect from 'vue-multiselect';
import alertMixin from 'shared/mixins/alertMixin';
import rtlMixin from 'shared/mixins/rtlMixin';
import Spinner from 'shared/components/Spinner.vue';
import MetricCard from './MetricCard.vue';
import { dailyWorkTasksAPI } from 'dashboard/api/dailyWork';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { findDailyTaskView } from 'dashboard/helper/dailyTaskCustomViews';

const PAGE_SIZE = 15;
const ALL_USERS_ID = 'all';

export default {
  name: 'DailyWorkTasks',
  components: { MetricCard, Multiselect, VeTable, VePagination, Spinner },
  mixins: [alertMixin, rtlMixin],
  props: {
    agents: {
      type: Array,
      default: () => [],
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    selectedUserId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      tasks: [],
      isLoading: false,
      completingId: null,
      selectedUser: null,
      includeCompleted: false,
      showRemarkModal: false,
      remarkModalMode: 'create',
      completionRemark: '',
      selectedCompletionTypes: [],
      completingRow: null,
      pageIndex: 1,
      nextGenerationAt: null,
      accountTimezone: 'Asia/Kolkata',
      meta: {
        page: 1,
        per_page: PAGE_SIZE,
        total_count: 0,
        pending_count: 0,
        completed_count: 0,
      },
      bodyRowEventsOption: Object.freeze({
        bodyRowEvents: ({ row }) => ({
          click: event => this.onRowClick(event, row),
        }),
      }),
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
    }),
    headerTitle() {
      return 'Daily Tasks';
    },
    rootComponent() {
      return this.embedded ? 'div' : MetricCard;
    },
    rootProps() {
      return this.embedded
        ? { class: 'daily-work-embedded' }
        : { header: this.headerTitle, isLive: false };
    },
    pageSize() {
      return this.meta.per_page || PAGE_SIZE;
    },
    ownUserOption() {
      const id = this.currentUser?.id;
      return {
        id: id == null || id === '' ? null : Number(id),
        name: this.currentUser?.name || 'Me',
      };
    },
    userFilterOptions() {
      const users = this.agents
        .map(user => {
          const id = user.id || user.user_id;
          if (id == null || id === '') return null;
          return {
            id: Number(id),
            name: user.name || user.user?.name || `User #${id}`,
          };
        })
        .filter(Boolean);
      if (
        this.ownUserOption.id &&
        !users.some(user => user.id === this.ownUserOption.id)
      ) {
        users.unshift(this.ownUserOption);
      }
      return [{ id: ALL_USERS_ID, name: 'All Users' }, ...users];
    },
    tableData() {
      return this.tasks.map(task => ({
        id: task.id,
        contactId: task.contact?.id,
        contact: task.contact?.name || `Contact #${task.contact?.id}`,
        company: task.contact?.company || '',
        taskType: this.taskTypesLabel(task),
        taskTypes: this.taskTypesFor(task),
        pendingTaskTypes: task.pending_task_types || this.taskTypesFor(task),
        lastIncomingAt: task.last_incoming_at,
        status: task.status === 'completed' ? 'Completed' : 'Pending',
        note: task.note || '',
        agent: task.agent?.name || '—',
        time: this.taskTime(task),
        assigned: this.formatDateTime(task.allocated_at || task.allocated_on),
        completed: task.status === 'completed',
        remark: task.remark || '',
        pendingFromPreviousDay: Boolean(task.pending_from_previous_day),
        completing: this.completingId === task.id,
      }));
    },
    displayTableData() {
      const rows = [...this.tableData];
      if ((this.meta.completed_count || 0) > 0) {
        rows.unshift({ id: '__completed_toolbar__', __isToolbar: true });
      }
      if (this.tableData.length === 0) {
        rows.push({ id: '__daily_work_empty__', __isEmpty: true });
      }
      return rows;
    },
    columns() {
      const align = this.isRTLView ? 'right' : 'left';
      return [
        {
          field: 'contact',
          key: 'contact',
          title: 'Name',
          align,
          width: 120,
          ellipsis: { showTitle: false, lineClamp: 1 },
          renderBodyCell: ({ row }) => this.renderFirstCell(row),
        },
        {
          field: 'company',
          key: 'company',
          title: 'Company',
          align,
          width: 100,
          ellipsis: { showTitle: false, lineClamp: 1 },
          renderBodyCell: ({ row }) =>
            row.__isToolbar || row.__isEmpty
              ? h('span')
              : this.renderEllipsisCell(row.company),
        },
        {
          field: 'taskType',
          key: 'taskType',
          title: 'Type',
          align,
          width: 150,
          ellipsis: { showTitle: false, lineClamp: 2 },
          renderBodyCell: ({ row }) =>
            row.__isToolbar || row.__isEmpty
              ? h('span')
              : this.renderTaskTypeCell(row),
        },
        {
          field: 'complete',
          key: 'complete',
          title: 'Status',
          align: 'center',
          width: 140,
          renderBodyCell: ({ row }) =>
            row.__isToolbar || row.__isEmpty
              ? h('span')
              : this.renderStatusActionCell(row),
        },
      ];
    },
    rowStyleOption() {
      return {
        clickHighlight: false,
        hoverHighlight: true,
      };
    },
    cellSpanOption() {
      return {
        bodyCellSpan: ({ row, column }) => this.bodyCellSpan(row, column),
      };
    },
    eventCustomOption() {
      return this.bodyRowEventsOption;
    },
    nextFillTime() {
      return this.formatFillTime(this.nextGenerationAt, {
        compact: this.embedded,
      });
    },
    nextFillTimeFull() {
      return this.formatFillTime(this.nextGenerationAt);
    },
    completedButtonLabel() {
      const count = this.meta.completed_count || 0;
      return this.includeCompleted
        ? "Hide Today's Completed Tasks"
        : `Load Today's Completed Tasks(${count})`;
    },
    emptyMessage() {
      if (this.includeCompleted) return 'No tasks.';
      const completed = this.meta.completed_count || 0;
      if (completed > 0) {
        return `There are ${completed} completed tasks from today.`;
      }
      return 'No tasks.';
    },
    isViewingRemark() {
      return this.remarkModalMode === 'view';
    },
    completionTypeOptions() {
      const row = this.completingRow;
      if (!row) return [];
      const types = row.pendingTaskTypes?.length
        ? row.pendingTaskTypes
        : row.taskTypes || [];
      return types.map(type => ({
        value: type,
        label: this.taskTypeLabel(type),
      }));
    },
    allCompletionTypesSelected() {
      return (
        this.completionTypeOptions.length > 0 &&
        this.selectedCompletionTypes.length === this.completionTypeOptions.length
      );
    },
    canSubmitCompletion() {
      return (
        Boolean((this.completionRemark || '').trim()) &&
        this.selectedCompletionTypes.length > 0
      );
    },
  },
  created() {
    if (this.embedded) {
      this.syncSelectedUserFromProp();
    } else {
      this.selectedUser = this.ownUserOption;
    }
  },
  watch: {
    userFilterOptions(options) {
      const selectedId = this.selectedUser?.id;
      if (selectedId == null || selectedId === ALL_USERS_ID) return;
      const match = options.find(option => option.id === Number(selectedId));
      if (match && this.selectedUser !== match) this.selectedUser = match;
    },
    selectedUserId() {
      if (!this.embedded) return;
      this.syncSelectedUserFromProp();
      this.pageIndex = 1;
      this.fetchTasks();
    },
    isLoading(value) {
      this.$emit('loading', value);
    },
  },
  mounted() {
    if (!this.embedded) this.selectedUser = this.ownUserOption;
    this.fetchTasks();
  },
  methods: {
    syncSelectedUserFromProp() {
      const id = this.selectedUserId;
      if (id === ALL_USERS_ID || id === 'all') {
        this.selectedUser =
          this.userFilterOptions.find(option => option.id === ALL_USERS_ID) || {
            id: ALL_USERS_ID,
            name: 'All Users',
          };
        return;
      }
      if (id == null || id === '') {
        this.selectedUser = this.ownUserOption;
        return;
      }
      const match = this.userFilterOptions.find(
        option => String(option.id) === String(id)
      );
      this.selectedUser = match || {
        id: Number(id),
        name: `User #${id}`,
      };
    },
    async openFullView() {
      const accountId =
        this.$route.params.accountId ?? this.$route.params.account_id;
      if (accountId == null || String(accountId).trim() === '') return;

      await this.$store.dispatch('customViews/get', 'contact');
      const views = this.$store.getters['customViews/getCustomViewsByFilterType'](
        'contact'
      );
      let match = null;
      if (this.isAdmin && this.selectedUser?.id === ALL_USERS_ID) {
        match = findDailyTaskView(views, 'all');
      } else {
        match = findDailyTaskView(
          views,
          this.selectedUser?.id || this.currentUser?.id
        );
      }
      if (match?.id) {
        this.$router.push({
          path: frontendURL(
            `accounts/${accountId}/contacts/custom_view/${match.id}`
          ),
        });
        return;
      }

      const query = {};
      if (this.isAdmin && this.selectedUser?.id === ALL_USERS_ID) {
        query.all_users = 'true';
      } else if (this.isAdmin && this.selectedUser?.id) {
        query.user_id = this.selectedUser.id;
      }

      this.$router.push({
        path: frontendURL(`accounts/${accountId}/contacts/daily-tasks`),
        query,
      });
    },
    reloadTasks() {
      this.pageIndex = 1;
      this.fetchTasks();
    },
    onUserFilterChange() {
      if (!this.selectedUser) this.selectedUser = this.ownUserOption;
      this.pageIndex = 1;
      this.fetchTasks();
    },
    taskFilterParams() {
      if (this.isAdmin && this.selectedUser?.id === ALL_USERS_ID) {
        return { all_users: true };
      }
      const userId = this.selectedUser?.id || this.currentUser?.id;
      return userId ? { user_id: userId } : {};
    },
    onPageChange(page) {
      this.pageIndex = page;
      this.fetchTasks();
    },
    async toggleCompleted() {
      this.includeCompleted = !this.includeCompleted;
      this.pageIndex = 1;
      await this.fetchTasks();
    },
    async fetchTasks({ silent = false } = {}) {
      if (!silent) this.isLoading = true;
      try {
        const { data } = await dailyWorkTasksAPI.getTasks({
          ...this.taskFilterParams(),
          page: this.pageIndex,
          per_page: PAGE_SIZE,
          include_pending: true,
          include_completed: this.includeCompleted,
        });
        this.tasks = this.sortTasks(data.tasks || []);
        this.meta = {
          page: data.meta?.page || this.pageIndex,
          per_page: data.meta?.per_page || PAGE_SIZE,
          total_count: data.meta?.total_count || 0,
          pending_count: data.meta?.pending_count || 0,
          completed_count: data.meta?.completed_count || 0,
        };
        this.nextGenerationAt = data.meta?.next_generation_at || null;
        this.accountTimezone = data.meta?.timezone || 'Asia/Kolkata';
      } catch (error) {
        this.showAlert(
          error?.response?.data?.error || 'Unable to load daily tasks'
        );
      } finally {
        this.isLoading = false;
      }
    },
    openRemarkModal(row) {
      if (this.completingId || row.completed) return;
      this.remarkModalMode = 'create';
      this.completingRow = row;
      this.completionRemark = '';
      this.selectedCompletionTypes = [...(row.pendingTaskTypes || row.taskTypes || [])];
      this.showRemarkModal = true;
    },
    openRemarkView(row) {
      this.remarkModalMode = 'view';
      this.completingRow = row;
      this.completionRemark = row.remark || '';
      this.showRemarkModal = true;
    },
    closeRemarkModal() {
      if (this.completingId) return;
      this.showRemarkModal = false;
      this.remarkModalMode = 'create';
      this.completionRemark = '';
      this.selectedCompletionTypes = [];
      this.completingRow = null;
    },
    toggleAllCompletionTypes() {
      if (this.allCompletionTypesSelected) {
        this.selectedCompletionTypes = [];
        return;
      }
      this.selectedCompletionTypes = this.completionTypeOptions.map(
        option => option.value
      );
    },
    async submitRemark() {
      const row = this.completingRow;
      const remark = (this.completionRemark || '').trim();
      const taskTypes = [...this.selectedCompletionTypes];
      if (
        !row ||
        row.completed ||
        this.completingId ||
        !remark ||
        !taskTypes.length
      ) {
        return;
      }
      this.completingId = row.id;
      try {
        await dailyWorkTasksAPI.complete(row.id, { remark, taskTypes });
        this.showAlert(
          taskTypes.length === (row.pendingTaskTypes || row.taskTypes || []).length
            ? 'Task completed'
            : 'Selected task types completed'
        );
        this.showRemarkModal = false;
        this.remarkModalMode = 'create';
        this.completionRemark = '';
        this.selectedCompletionTypes = [];
        this.completingRow = null;
        await this.fetchTasks({ silent: true });
      } catch (error) {
        this.showAlert(
          error?.response?.data?.error || 'Unable to complete task'
        );
      } finally {
        this.completingId = null;
      }
    },
    onRowClick(event, row) {
      if (row.__isToolbar || row.__isEmpty) return;
      const target = event?.target;
      if (target?.closest?.('button, a, input, textarea, select')) return;
      if (row.contactId) this.$emit('open-contact', row.contactId);
    },
    bodyCellSpan(row, column) {
      const cols = this.columns;
      if (!cols?.length) return { rowspan: 1, colspan: 1 };
      if (row.__isToolbar || row.__isEmpty) {
        if (column.key === cols[0].key) {
          return { rowspan: 1, colspan: cols.length };
        }
        return { rowspan: 0, colspan: 0 };
      }
      return { rowspan: 1, colspan: 1 };
    },
    sortTasks(tasks) {
      return tasks.slice().sort((left, right) => {
        const completedDelta = this.completedSortGroup(left) - this.completedSortGroup(right);
        if (completedDelta !== 0) return completedDelta;
        if (left.status === 'completed' && right.status === 'completed') {
          const rightCompleted = this.completedAtValue(right);
          const leftCompleted = this.completedAtValue(left);
          if (rightCompleted !== leftCompleted) return rightCompleted - leftCompleted;
        }
        const priorityDelta = this.listPriorityGroup(left) - this.listPriorityGroup(right);
        if (priorityDelta !== 0) return priorityDelta;
        if (this.hasNewMessage(left) && this.hasNewMessage(right)) {
          const incomingDelta = this.incomingSortValue(right) - this.incomingSortValue(left);
          if (incomingDelta !== 0) return incomingDelta;
        }
        if (this.isNewLeadGroup(left) && this.isNewLeadGroup(right)) {
          const leadDelta = this.newLeadSortValue(right) - this.newLeadSortValue(left);
          if (leadDelta !== 0) return leadDelta;
        }
        const delta = this.taskSortValue(left) - this.taskSortValue(right);
        if (delta !== 0) return delta;
        return (left.id || 0) - (right.id || 0);
      });
    },
    completedSortGroup(task) {
      return task.status === 'completed' ? 0 : 1;
    },
    completedAtValue(task) {
      if (!task.completed_at) return 0;
      const value = new Date(task.completed_at).getTime();
      return Number.isNaN(value) ? 0 : value;
    },
    taskTypesFor(task) {
      if (Array.isArray(task.pending_task_types) && task.pending_task_types.length) {
        return task.pending_task_types;
      }
      if (Array.isArray(task.task_types) && task.task_types.length) {
        return task.task_types;
      }
      return task.task_type ? [task.task_type] : [];
    },
    hasNewMessage(task) {
      return this.taskTypesFor(task).includes('new_message');
    },
    isNewLeadGroup(task) {
      return (
        !this.hasNewMessage(task) && this.taskTypesFor(task).includes('new_lead')
      );
    },
    listPriorityGroup(task) {
      const types = this.taskTypesFor(task);
      if (types.includes('new_message')) return 0;
      if (types.includes('new_lead')) return 1;
      if (types.includes('demo') || types.includes('reminder')) return 2;
      return 3;
    },
    incomingSortValue(task) {
      const raw = task.last_incoming_at || task.updated_at;
      if (!raw) return 0;
      const value = new Date(raw).getTime();
      return Number.isNaN(value) ? 0 : value;
    },
    newLeadSortValue(task) {
      const raw = task.scheduled_at || task.allocated_at || task.created_at;
      if (!raw) return 0;
      const value = new Date(raw).getTime();
      return Number.isNaN(value) ? 0 : value;
    },
    taskSortValue(task) {
      if (task.scheduled_at) {
        const scheduled = new Date(task.scheduled_at).getTime();
        if (!Number.isNaN(scheduled)) return scheduled;
      }
      if (task.due_on) {
        const due = new Date(`${task.due_on}T00:00:00`).getTime();
        if (!Number.isNaN(due)) return due;
      }
      return Number.MAX_SAFE_INTEGER;
    },
    taskTypeLabel(type) {
      return (
        {
          new_message: 'New message received',
          demo: 'Meeting',
          reminder: 'Reminder',
          new_lead: 'New Lead',
          non_active_lead: 'Non-active Lead',
        }[type] || type
      );
    },
    taskTypesLabel(task) {
      return this.taskTypesFor(task)
        .map(type => this.taskTypeLabel(type))
        .join(', ');
    },
    taskTime(task) {
      if (task.scheduled_at) return this.formatDateTime(task.scheduled_at);
      return task.due_on || '—';
    },
    formatDateTime(iso) {
      if (!iso) return '—';
      const date = iso instanceof Date ? iso : new Date(iso);
      if (Number.isNaN(date.getTime())) return '—';
      try {
        return new Intl.DateTimeFormat(undefined, {
          timeZone: this.accountTimezone,
          month: 'numeric',
          day: 'numeric',
          year: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).format(date);
      } catch (_) {
        return date.toLocaleString();
      }
    },
    formatFillTime(iso, { compact = false } = {}) {
      if (!iso) return '—';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '—';
      try {
        return new Intl.DateTimeFormat(undefined, {
          timeZone: this.accountTimezone,
          weekday: compact ? 'short' : 'long',
          month: 'numeric',
          day: 'numeric',
          ...(compact ? {} : { year: '2-digit' }),
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).format(date);
      } catch (_) {
        return date.toLocaleString();
      }
    },
    tooltipOptions(content) {
      return {
        content,
        classes: ['vue-tooltip-theme'],
        delay: { show: 150, hide: 0 },
      };
    },
    renderFirstCell(row) {
      if (row.__isToolbar) {
        return h('div', { class: 'daily-work-toolbar-row' }, [
          h(
            'button',
            {
              class: 'daily-work-pending-btn',
              attrs: { type: 'button' },
              on: { click: event => {
                event.stopPropagation();
                this.toggleCompleted();
              } },
            },
            this.completedButtonLabel
          ),
        ]);
      }
      if (row.__isEmpty) {
        return h('div', { class: 'daily-work-empty-row' }, [
          h('h3', { class: 'daily-work-empty-row__text' }, this.emptyMessage),
        ]);
      }
      return this.renderEllipsisCell(row.contact);
    },
    renderTaskTypeCell(row) {
      const labels = (row.taskTypes || []).map(type => this.taskTypeLabel(type));
      if (!labels.length) return this.renderEllipsisCell(row.taskType);
      const tooltip = labels.join(', ');
      return h(
        'div',
        {
          class: 'daily-work-task-types',
          directives: [{ name: 'tooltip', value: this.tooltipOptions(tooltip) }],
        },
        labels.map(label =>
          h('div', { class: 'daily-work-task-type' }, label)
        )
      );
    },
    renderEllipsisCell(value) {
      const text = value == null || value === '' ? '—' : String(value);
      return h('div', { class: 'daily-work-ellipsis-cell' }, [
        h(
          'span',
          {
            class: 'daily-work-ellipsis-cell__text',
            directives: [{ name: 'tooltip', value: this.tooltipOptions(text) }],
          },
          text
        ),
      ]);
    },
    renderStatusActionCell(row) {
      if (row.completed) {
        return h('div', { class: 'daily-work-status-wrap' }, [
          h(
            'button',
            {
              class: 'daily-work-status-checkbox daily-work-status-checkbox--checked',
              attrs: {
                type: 'button',
                'aria-label': 'View remark',
              },
              directives: [
                { name: 'tooltip', value: this.tooltipOptions('View remark') },
              ],
              on: {
                click: event => {
                  event.stopPropagation();
                  this.openRemarkView(row);
                },
              },
            },
            [this.renderCheckedCheckboxIcon()]
          ),
        ]);
      }

      return h('div', { class: 'daily-work-status-wrap' }, [
        h(
          'button',
          {
            class: {
              'daily-work-status-checkbox': true,
              'is-loading': row.completing,
            },
            attrs: {
              type: 'button',
              'aria-label': 'Complete task',
              disabled: row.completing,
            },
            on: {
              click: event => {
                event.stopPropagation();
                this.openRemarkModal(row);
              },
            },
          },
          [this.renderEmptyCheckboxIcon()]
        ),
      ]);
    },
    checkboxIconAttrs() {
      return {
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        width: '20',
        height: '20',
        'aria-hidden': 'true',
        focusable: 'false',
      };
    },
    renderEmptyCheckboxIcon() {
      return h('svg', { attrs: this.checkboxIconAttrs() }, [
        h('rect', {
          attrs: {
            x: '4.5',
            y: '4.5',
            width: '15',
            height: '15',
            rx: '3',
            fill: 'none',
            stroke: 'currentColor',
            'stroke-width': '1.5',
          },
        }),
      ]);
    },
    renderCheckedCheckboxIcon() {
      return h('svg', { attrs: this.checkboxIconAttrs() }, [
        h('rect', {
          attrs: {
            x: '4.5',
            y: '4.5',
            width: '15',
            height: '15',
            rx: '3',
            fill: 'currentColor',
            stroke: 'currentColor',
            'stroke-width': '1.5',
          },
        }),
        h('path', {
          attrs: {
            d: 'M8.25 12.25l2.25 2.25 5.25-5.25',
            fill: 'none',
            stroke: '#ffffff',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round',
          },
        }),
      ]);
    },
  },
};
</script>

<style lang="scss" scoped>
.daily-work-embedded {
  width: 100%;
  min-width: 0;
}

.daily-work-container.daily-work-container--embedded {
  flex: 0 0 auto;
  height: var(--overview-leads-body-height, calc(602px + 1rem + 1.21875rem - 2px));
}

.daily-work-container--embedded .daily-work-table-wrap {
  flex: 1 1 auto;
  height: 0;
  min-height: 0;
  overflow: hidden;
}

.daily-work-container--embedded .daily-work-footer {
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  min-height: auto;
  flex-shrink: 0;
}

.daily-work-container--embedded .daily-work-next-task {
  width: 100%;
  height: 1.21875rem;
  line-height: 1.21875rem;
  flex-shrink: 0;
  overflow: hidden;
}

.daily-work-container--embedded .daily-work-next-task__timer {
  overflow: hidden;
  text-overflow: ellipsis;
}

.daily-work-container--embedded .table-pagination {
  margin-left: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  overflow-x: auto;
}

.daily-work-reload-btn {
  min-width: 28px;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.daily-work-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.daily-work-agent-filter {
  width: 200px;
}

:deep(.daily-work-full-view-btn.button.button--only-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-top: 0;
  padding-bottom: 0;
}

:deep(.daily-work-full-view-btn .icon) {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

:deep(.daily-work-full-view-btn svg) {
  display: block;
}

.daily-work-container {
  @apply flex flex-col flex-1;
  position: relative;
}

.daily-work-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 57px;
  margin-top: 1rem;
}

.daily-work-next-task {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.8125rem;
  color: var(--s-700);
}

.daily-work-next-task__label {
  font-weight: 600;
  white-space: nowrap;
}

.daily-work-next-task__timer {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  white-space: nowrap;
  color: var(--w-700, #40518a);
}

.daily-work-table-wrap {
  height: 480px;
  position: relative;
}

.daily-work--modal-open .daily-work-table-wrap {
  pointer-events: none;
}

.daily-work--modal-open :deep(.ve-table-body-tr),
.daily-work--modal-open :deep(.ve-table-body-tr:hover) {
  background-color: transparent !important;
}

.daily-work-table-wrap :deep(.ve-table-container) {
  overflow-x: hidden !important;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.daily-work-table-wrap :deep(.ve-table-container::-webkit-scrollbar) {
  width: 0;
  height: 0;
  display: none;
}

.daily-work-empty-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  white-space: nowrap;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 300;
  color: var(--s-700);
}

.table-pagination {
  @apply ml-auto text-right;
  flex-shrink: 0;
}

.ve-table {
  &:deep {
    th.ve-table-header-th {
      font-size: var(--font-size-mini) !important;
      padding: var(--space-small) var(--space-small) !important;
    }

    td.ve-table-body-td {
      padding: var(--space-one) var(--space-small) !important;
    }
  }
}

:deep(.daily-work-ellipsis-cell) {
  overflow: hidden;
  max-width: 100%;
}

:deep(.daily-work-ellipsis-cell__text) {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

:deep(.ve-table-body-tr) {
  cursor: pointer;
}

:deep(.ve-table-body-tr:hover) {
  background-color: rgba(15, 23, 42, 0.04);
}

:deep(.daily-work-toolbar-row) {
  display: flex;
  width: 100%;
  justify-content: center;
}

:deep(.daily-work-pending-btn) {
  width: 100%;
  min-height: 28px;
  padding: 0.35rem 0.75rem;
  border: 0;
  border-radius: 0.375rem;
  background: var(--s-50, #f1f5f9);
  color: var(--s-800, #1e293b);
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
}

:deep(.daily-work-pending-btn:hover) {
  background: var(--s-75, #e2e8f0);
}

:deep(.daily-work-empty-row) {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: var(--space-normal) var(--space-two);
  text-align: center;
}

:deep(.daily-work-empty-row__text) {
  margin: 0;
  width: 100%;
  font-size: 1.5rem;
  font-weight: 300;
  color: var(--s-700);
}

:deep(.daily-work-status-wrap) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

:deep(.daily-work-status-checkbox) {
  @apply inline-flex items-center justify-center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
}

:deep(.daily-work-status-checkbox svg) {
  display: block;
}

:deep(.daily-work-status-checkbox:hover) {
  color: #64748b;
}

:deep(.daily-work-status-checkbox--checked) {
  color: #059669;
}

:deep(.daily-work-status-checkbox--checked:hover) {
  color: #047857;
}

:deep(.daily-work-status-checkbox.is-loading),
:deep(.daily-work-status-checkbox:disabled) {
  opacity: 0.45;
  cursor: default;
}

:deep(.daily-work-task-types) {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

:deep(.daily-work-task-type) {
  font-size: 13px;
  line-height: 1.25;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.ve-pagination) {
  @apply bg-transparent dark:bg-transparent;
}

:deep(.ve-pagination-select) {
  @apply hidden;
}

.daily-work-loader-overlay {
  @apply bg-white/90 dark:bg-slate-900/90;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.daily-work-loader-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.daily-work-remark-input {
  width: 100%;
  min-height: 96px;
  margin-top: 0.35rem;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  font-size: 14px;
  resize: vertical;
}

.daily-work-complete-types {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.daily-work-complete-types__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.daily-work-complete-types__toggle {
  border: 0;
  background: transparent;
  color: #40518a;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.daily-work-complete-type-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  color: #334155;
  cursor: pointer;
}

.daily-work-complete-type-option__checkbox {
  margin: 0;
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
}

.daily-work-complete-type-option__label {
  line-height: 1.4;
}

.daily-work-remark-field {
  display: block;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: #334155;
}

.daily-work-remark-input:focus {
  outline: none;
  border-color: #40518a;
}

.daily-work-remark-input[readonly] {
  background: #f8fafc;
  cursor: default;
}

.dark .daily-work-remark-input {
  border-color: #475569;
  background: #1e293b;
  color: #f1f5f9;
}
</style>
